import * as React from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import ManagePaymentModal from "components/manage-payment-modal";
import PinEntryModal from "components/pin-entry-modal";
import TradeInDetails from "components/trade-in-details";
import { useLocation, useNavigate } from "react-router-dom";
import { useContext, useState } from "react";
import AuthContext from "contexts/AuthContext";
import { getFeatureFlags } from "utils/tokenUtils";
import { useGenerateShipping } from "api/use-shipping";
import { AnimatePresence } from "framer-motion";
import AccountModal from "components/account-modal";
import { useAddStatus } from "api/tes/useAddStatus";
import { getQueryParams } from "lib/utils";

const TradeInCard = ({
  tradein,
  customerName,
  address,
  email,
  tradeInId,
  dateCreated,
  dateVerified,
  verifiedQuantity,
  originalEstimate,
  paymentDetails,
  location,
  appointment,
  currentLocation,
  forceOpen = false,
  createdAt,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);
  const featureFlags = getFeatureFlags(authTokens);
  const [isModalOpen, setIsModalOpen] = React.useState(forceOpen);
  const [isPinModalOpen, setIsPinModalOpen] = React.useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = React.useState(false);
  const [isGeneratingLabel, setIsGeneratingLabel] = useState(false);
  const [showEditSellerModal, setShowEditSellerModal] = useState(false);

  const isDropoffStore = ["in_store_nj", "in_store_pa"].includes(
    tradein?.data?.returnMethod
  );

  // Extract shipping label data from GraphQL structure
  const shippingLabel =
    tradein?.data?.u_ups_shipping_label_encoded ||
    tradein?.data?.u_lego_dhl_label_b64_encoded;
  const originalLabelUrl =
    tradein?.data?.u_ups_shipping_label_url ||
    tradein?.shipping_metadatas?.items?.find((item) => item.key === "label_url")
      ?.value;

  // Set up shipping generation hook similar to shipping-modal
  const generateShipping = useGenerateShipping({
    onSuccess: (data) => {
      setIsGeneratingLabel(false);
      // Extract the label URL from the response and open it immediately
      const shippingData = data?.data?.[0];
      const originalLabelUrl = shippingData?.shipping_metadatas?.items?.find(
        (item) => item.key === "label_url"
      )?.value;

      if (originalLabelUrl && tradeInId) {
        try {
          const url = new URL(originalLabelUrl);
          const returnID = url.searchParams.get("returnID");

          if (returnID && authTokens?.access_token) {
            const labelUrl = `${window.location.origin}/api/pdf/shipping-label-no-instructions?returnID=${returnID}&token=${authTokens.access_token}`;
            // Open PDF in new tab for printing
            window.open(labelUrl, "_blank");
          } else {
            window.open(originalLabelUrl, "_blank");
          }
        } catch (error) {
          console.error("Error constructing label URL:", error);
          window.open(originalLabelUrl, "_blank");
        }
      }
    },
    onError: () => {
      setIsGeneratingLabel(false);
      window.alert("Failed to generate shipping label");
    },
  });

  // Construct label URL similar to my-trade-ins
  const labelUrl = React.useMemo(() => {
    if (originalLabelUrl && tradeInId) {
      try {
        const url = new URL(originalLabelUrl);
        const returnID = url.searchParams.get("returnID");

        if (returnID && authTokens?.access_token) {
          return `${window.location.origin}/api/pdf/shipping-label-no-instructions?returnID=${returnID}&token=${authTokens.access_token}`;
        }
        return originalLabelUrl;
      } catch (error) {
        console.error("Error constructing label URL:", error);
        return originalLabelUrl;
      }
    }
    return originalLabelUrl;
  }, [originalLabelUrl, tradeInId, authTokens.access_token]);

  const handleClose = () => {
    setIsModalOpen(false);
    // If this was opened via URL, navigate back to trade-ins list
    if (forceOpen) {
      navigate(t("urls.store_trade_ins"));
    }
  };

  const handlePrintShippingLabel = () => {
    // Check if we have a shipping label to print
    const shippingPresent = !!(shippingLabel || originalLabelUrl);

    if (shippingPresent && labelUrl) {
      // Open PDF in new tab for printing - same as my-trade-ins
      window.open(labelUrl, "_blank");
    } else {
      // No shipping label available - generate one like shipping-modal
      if (isGeneratingLabel) return; // Prevent double-clicks

      // Extract customer ID and takeback ID for shipping generation
      const customerId = tradein?.customer_id;
      const takebackId = tradein?.id;

      if (!customerId || !takebackId) {
        window.alert(
          "Missing seller or takeback information required for shipping label generation"
        );
        return;
      }

      setIsGeneratingLabel(true);
      generateShipping.mutate({
        takeback_id: takebackId,
        customer_id: customerId,
        type: "MAIL_LABEL", // Generate printable shipping label
      });
    }
  };

  const { mutate: addStatus } = useAddStatus();
  const destination = isDropoffStore
    ? `/en-US/store/trade-in-weight/dropoff`
    : t("urls.store_trade_in_weight");
  // Get query params
  const routerLocation = useLocation();
  const queryParams = getQueryParams(routerLocation.search);

  const handleStartTradeIn = async () => {
    // Trade-in not verified yet - update status and navigate to weight screen
    const receivedAt = new Date().toISOString();
    localStorage.setItem("receivedAt", receivedAt);

    // Update takeback status for checkin and await completion
    await addStatus({
      id: tradein.id,
      input: {
        status: "in-store-checkin",
        parent_status: "processing",
        updated_at: parseInt(Date.now() / 1000),
      },
    });

    // Navigate after status update is complete
    const updatedQueryParams = { ...queryParams, id: tradein.id };
    navigate({
      pathname: destination,
      search: new URLSearchParams(updatedQueryParams).toString(),
    });
  };

  return (
    <>
      {isModalOpen && (
        <div className="inset-0 bg-[#ffffff] overflow-y-auto w-full z-50">
          <div className="relative min-h-full">
            <div className="relative min-h-full flex flex-col">
              <div className="flex-1 px-6 pt-10 pb-32 mx-auto w-full md:px-24 lg:px-40">
                <h1 className="text-4xl font-bold mb-1">Trade In</h1>
                <div className="text-lg text-tertiary mb-8">
                  {tradein?.takeback_processing_history_list?.some(
                    (history) => history.parent_status === "processed"
                  )
                    ? "Payment Issued"
                    : "Trade-in Created"}
                </div>

                <TradeInDetails
                  customerName={customerName}
                  address={address}
                  email={email}
                  tradeInId={tradeInId}
                  dateCreated={dateCreated}
                  dateVerified={dateVerified}
                  verifiedQuantity={verifiedQuantity}
                  originalEstimate={originalEstimate}
                  paymentDetails={paymentDetails}
                  location={location}
                  appointment={appointment}
                  currentLocation={currentLocation}
                  takebackIsVerified={true}
                  showEditSeller={true}
                  showManagePayment={true}
                  onEditSeller={() => setShowEditSellerModal(true)}
                  onManagePayment={() => setIsPinModalOpen(true)}
                  isDropoffStore={isDropoffStore}
                  featureFlags={featureFlags}
                  createdAt={createdAt}
                />
              </div>

              {/* Modal Footer */}
              <div className="flex justify-between gap-8 items-center px-6 md:px-24 lg:px-40 pb-4 pt-4 mx-auto mt-auto fixed bottom-[87px] sm:bottom-[105px] bg-white w-[100vw] left-0 right-0 border-t border-gray-300">
                <button
                  className="border border-gray-300 rounded-full px-6 py-2 text-lg hover:bg-gray-100"
                  onClick={handleClose}
                >
                  Close
                </button>
                {isDropoffStore && dateVerified ? (
                  <button
                    className="bg-blue text-white rounded-full px-6 py-2 text-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
                    onClick={handlePrintShippingLabel}
                    disabled={isGeneratingLabel}
                  >
                    {isGeneratingLabel
                      ? "Generating Label..."
                      : "Print Shipping Label"}
                  </button>
                ) : !dateVerified ? (
                  <button
                    className="bg-blue text-white rounded-full px-6 py-2 text-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
                    onClick={handleStartTradeIn}
                  >
                    Start Trade-In
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PIN Entry Modal */}
      <PinEntryModal
        isOpen={isPinModalOpen}
        onClose={() => setIsPinModalOpen(false)}
        onSuccess={() => {
          setIsPinModalOpen(false);
          setIsPaymentModalOpen(true);
        }}
        title="Manage payment"
      />

      {/* Manage Payment Modal */}
      <ManagePaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        takeback={tradein}
      />

      {/* Edit Seller Modal for editing customer details */}
      <AnimatePresence>
        <AccountModal
          onClose={() => setShowEditSellerModal(false)}
          show={showEditSellerModal}
          customerId={tradein?.customer_id}
        />
      </AnimatePresence>
    </>
  );
};

TradeInCard.propTypes = {
  tradein: PropTypes.object.isRequired,
  customerName: PropTypes.string.isRequired,
  address: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  tradeInId: PropTypes.string.isRequired,
  dateCreated: PropTypes.string.isRequired,
  dateVerified: PropTypes.string,
  verifiedQuantity: PropTypes.string.isRequired,
  originalEstimate: PropTypes.string,
  paymentDetails: PropTypes.string,
  location: PropTypes.string.isRequired,
  appointment: PropTypes.object,
  currentLocation: PropTypes.object,
  forceOpen: PropTypes.bool,
};

TradeInCard.defaultProps = {
  completed: false,
};

export default TradeInCard;
